import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Users, Plus, Edit2, Trash2, X } from 'lucide-react';
import { authApi, suppliersApi } from '../services/api';

const UsersPage = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    supplier_ids: [],
    is_active: true
  });

  // Загрузка пользователей
  const { data: users, isLoading: loadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await authApi.getUsers();
      return res.data;
    }
  });

  // Загрузка поставщиков
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-all'],
    queryFn: async () => {
      const res = await suppliersApi.getAll();
      return res.data;
    }
  });

  // Функция для форматирования ошибок
  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    return 'Произошла ошибка';
  };

  // Создание пользователя
  const createMutation = useMutation({
    mutationFn: (data) => authApi.createUser(data),
    onSuccess: () => {
      toast.success('Пользователь создан');
      queryClient.invalidateQueries(['users']);
      closeModal();
    },
    onError: (err) => toast.error(formatError(err))
  });

  // Обновление пользователя
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => authApi.updateUser(id, data),
    onSuccess: () => {
      toast.success('Пользователь обновлен');
      queryClient.invalidateQueries(['users']);
      closeModal();
    },
    onError: (err) => toast.error(formatError(err))
  });

  // Удаление пользователя
  const deleteMutation = useMutation({
    mutationFn: (id) => authApi.deleteUser(id),
    onSuccess: () => {
      toast.success('Пользователь удален');
      queryClient.invalidateQueries(['users']);
    },
    onError: (err) => toast.error(formatError(err))
  });

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      supplier_ids: [],
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '', // Не заполняем пароль при редактировании
      role: user.role,
      supplier_ids: user.supplier_ids || [],
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    if (showModal) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showModal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Подготовка данных
    const submitData = {
      ...formData,
      supplier_ids: formData.supplier_ids || []
    };

    // При редактировании не отправляем пустой пароль
    if (editingUser && !formData.password) {
      delete submitData.password;
    }

    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (user) => {
    if (window.confirm(`Удалить пользователя ${user.username}?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  if (loadingUsers) {
    return <div className="card">Загрузка...</div>;
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Users size={24} />
            <h2 style={{ margin: 0 }}>Пользователи</h2>
          </div>
          <button onClick={openCreateModal} className="btn btn-primary">
            <Plus size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Создать пользователя
          </button>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Логин</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Поставщики</th>
                <th>Статус</th>
                <th>Создан</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users?.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td><strong>{user.username}</strong></td>
                  <td>{user.email || '—'}</td>
                  <td>
                    <span className={`badge ${user.role === 'admin' ? 'badge-danger' : 'badge-primary'}`}>
                      {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                    </span>
                  </td>
                  <td>
                    {user.supplier_names && user.supplier_names.length > 0 
                      ? user.supplier_names.join(', ')
                      : '—'}
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}>
                      {user.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString('ru-RU')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openEditModal(user)}
                        className="btn btn-sm btn-secondary"
                        title="Редактировать"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="btn btn-sm btn-danger"
                        title="Удалить"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Модальное окно */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Редактировать пользователя' : 'Создать пользователя'}</h3>
              <button onClick={closeModal} className="btn-icon">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Логин *</label>
                <input
                  type="text"
                  className="input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Пароль {!editingUser && '*'}</label>
                <input
                  type="password"
                  className="input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Оставьте пустым, чтобы не менять' : ''}
                />
              </div>

              <div className="form-group">
                <label>Роль *</label>
                <select
                  className="input"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="user">Пользователь</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>

              {formData.role === 'user' && (
                <div className="form-group">
                  <label>Поставщики</label>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e1e8ed', borderRadius: '6px', padding: '8px' }}>
                    {suppliers?.map(supplier => (
                      <label key={supplier.id} style={{ display: 'block', padding: '4px 0', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.supplier_ids.includes(supplier.id)}
                          onChange={(e) => {
                            const newIds = e.target.checked
                              ? [...formData.supplier_ids, supplier.id]
                              : formData.supplier_ids.filter(id => id !== supplier.id);
                            setFormData({ ...formData, supplier_ids: newIds });
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {supplier.name}
                      </label>
                    ))}
                  </div>
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Выберите поставщиков, для которых пользователь может загружать файлы
                  </small>
                </div>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Активен</span>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Отмена
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingUser ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
